import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import Cookies from 'js-cookie'
import { message } from 'antd'
import { authApi } from '../services/api'

interface User {
  id: string
  email: string
  name: string
  role: string
  twoFactorEnabled?: boolean
  twoFactorSetupAt?: string
}

interface AuthContextType {
  user: User | null
  token: string | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (email: string, password: string, twoFactorToken?: string, backupCode?: string) => Promise<{ success: boolean; requiresTwoFactor?: boolean }>
  logout: () => void
  refreshToken: () => Promise<boolean>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Initialize auth state from cookies
  useEffect(() => {
    const initAuth = async () => {
      const storedToken = Cookies.get('auth_token')
      if (storedToken) {
        setToken(storedToken)
        try {
          const userData = await authApi.getProfile()
          setUser(userData)
        } catch (error) {
          console.error('Failed to fetch user profile:', error)
          Cookies.remove('auth_token')
          setToken(null)
        }
      }
      setIsLoading(false)
    }

    initAuth()
  }, [])

  const login = async (email: string, password: string, twoFactorToken?: string, backupCode?: string): Promise<{ success: boolean; requiresTwoFactor?: boolean }> => {
    try {
      setIsLoading(true)
      const response = await authApi.login(email, password, twoFactorToken, backupCode)
      
      if (response.requiresTwoFactor) {
        return { success: false, requiresTwoFactor: true }
      }
      
      if (response.token && response.user) {
        setToken(response.token)
        setUser(response.user)
        
        // Store token in cookie with 7 days expiry
        Cookies.set('auth_token', response.token, { 
          expires: 7, 
          secure: process.env.NODE_ENV === 'production',
          sameSite: 'strict'
        })
        
        message.success('Login successful')
        return { success: true }
      }
      return { success: false }
    } catch (error: any) {
      console.error('Login error:', error)
      message.error(error?.response?.data?.message || 'Login failed')
      return { success: false }
    } finally {
      setIsLoading(false)
    }
  }

  const logout = () => {
    setUser(null)
    setToken(null)
    Cookies.remove('auth_token')
    message.success('Logged out successfully')
  }

  const refreshToken = async (): Promise<boolean> => {
    try {
      const response = await authApi.refreshToken()
      if (response.token) {
        setToken(response.token)
        Cookies.set('auth_token', response.token, { 
          expires: 7, 
          secure: process.env.NODE_ENV === 'production',
          sameSite: 'strict'
        })
        return true
      }
      return false
    } catch (error) {
      console.error('Token refresh failed:', error)
      logout()
      return false
    }
  }

  const value: AuthContextType = {
    user,
    token,
    isLoading,
    isAuthenticated: !!user && !!token,
    login,
    logout,
    refreshToken,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
} 